import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/layout/AppShell";
import { ProtectedRoute } from "./components/layout/ProtectedRoute";
import { LoadingState } from "./design-system";
import { AcceptInvitationPage } from "./pages/auth/AcceptInvitationPage";
import { AuthLayout } from "./pages/auth/AuthLayout";
import { LoginPage } from "./pages/auth/LoginPage";
import { RegisterPage } from "./pages/auth/RegisterPage";
import { HomePage } from "./pages/public/HomePage";
import { ReferralPage } from "./pages/public/ReferralPage";

const AdminDashboardPage = lazy(() =>
  import("./pages/admin/AdminDashboardPage").then((m) => ({
    default: m.AdminDashboardPage,
  })),
);
const AdminLeadsPage = lazy(() =>
  import("./pages/admin/AdminLeadsPage").then((m) => ({
    default: m.AdminLeadsPage,
  })),
);
const AdminPartnersPage = lazy(() =>
  import("./pages/admin/AdminPartnersPage").then((m) => ({
    default: m.AdminPartnersPage,
  })),
);
const AdminPointsPage = lazy(() =>
  import("./pages/admin/AdminPointsPage").then((m) => ({
    default: m.AdminPointsPage,
  })),
);
const AdminRewardsPage = lazy(() =>
  import("./pages/admin/AdminRewardsPage").then((m) => ({
    default: m.AdminRewardsPage,
  })),
);
const PartnerDashboardPage = lazy(() =>
  import("./pages/partner/PartnerDashboardPage").then((m) => ({
    default: m.PartnerDashboardPage,
  })),
);
const PartnerLeadsPage = lazy(() =>
  import("./pages/partner/PartnerLeadsPage").then((m) => ({
    default: m.PartnerLeadsPage,
  })),
);
const PartnerSharePage = lazy(() =>
  import("./pages/partner/PartnerSharePage").then((m) => ({
    default: m.PartnerSharePage,
  })),
);
const PartnerRewardsPage = lazy(() =>
  import("./pages/partner/PartnerRewardsPage").then((m) => ({
    default: m.PartnerRewardsPage,
  })),
);

function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<LoadingState label="Carregando página…" />}>
      {children}
    </Suspense>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/r/:slug" element={<ReferralPage />} />
      <Route element={<AuthLayout />}>
        <Route path="/parceiro/entrar" element={<LoginPage role="PARTNER" />} />
        <Route path="/parceiro/cadastrar" element={<RegisterPage />} />
        <Route
          path="/parceiro/aceitar-convite"
          element={<AcceptInvitationPage />}
        />
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="/admin/entrar" element={<LoginPage role="ADMIN" />} />
      </Route>
      <Route element={<ProtectedRoute role="ADMIN" />}>
        <Route element={<AppShell mode="admin" />}>
          <Route
            path="/admin"
            element={
              <LazyPage>
                <AdminDashboardPage />
              </LazyPage>
            }
          />
          <Route
            path="/admin/leads"
            element={
              <LazyPage>
                <AdminLeadsPage />
              </LazyPage>
            }
          />
          <Route
            path="/admin/parceiros"
            element={
              <LazyPage>
                <AdminPartnersPage />
              </LazyPage>
            }
          />
          <Route
            path="/admin/pontos"
            element={
              <LazyPage>
                <AdminPointsPage />
              </LazyPage>
            }
          />
          <Route
            path="/admin/recompensas"
            element={
              <LazyPage>
                <AdminRewardsPage />
              </LazyPage>
            }
          />
        </Route>
      </Route>
      <Route element={<ProtectedRoute role="PARTNER" />}>
        <Route element={<AppShell mode="partner" />}>
          <Route
            path="/parceiro"
            element={
              <LazyPage>
                <PartnerDashboardPage />
              </LazyPage>
            }
          />
          <Route
            path="/parceiro/indicacoes"
            element={
              <LazyPage>
                <PartnerLeadsPage />
              </LazyPage>
            }
          />
          <Route
            path="/parceiro/divulgar"
            element={
              <LazyPage>
                <PartnerSharePage />
              </LazyPage>
            }
          />
          <Route
            path="/parceiro/recompensas"
            element={
              <LazyPage>
                <PartnerRewardsPage />
              </LazyPage>
            }
          />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
